/**
 * Checkout GCLID Injection Helper
 * 
 * Handles GCLID injection into checkout forms with support for:
 * - Dynamic forms (Sprig partials, multi-step checkout)
 * - Idempotent injection (prevents double injection)
 * - Form submit safety net
 * - Sprig/HTMX update events
 */

import { getGclid } from './gclid.js';

// Track injected fields to prevent double injection
const injectedFields = new WeakSet();

/**
 * Inject GCLID into checkout form
 * Idempotent - safe to call multiple times, won't double-inject
 * This function is made available globally for use in Twig templates
 * 
 * @returns {boolean} True if injection was successful
 */
export function injectGclidIntoCheckout() {
    try {
        const gclid = getGclid();

        if (!gclid) {
            if (window.DEBUG_GCLID) {
                console.log('[GCLID] No GCLID available for checkout injection');
            }
            return false;
        }

        // Find all checkout GCLID fields (in case there are multiple forms/steps)
        const gclidFields = document.querySelectorAll('[name="fields[googleClickId]"]');

        if (gclidFields.length === 0) {
            if (window.DEBUG_GCLID) {
                console.log('[GCLID] Checkout GCLID field not found');
            }
            return false;
        }

        let injected = false;
        gclidFields.forEach(function (gclidField) {
            // Prevent double injection - check if already injected
            if (injectedFields.has(gclidField)) {
                // Check if field already has correct value
                if (gclidField.value === gclid) {
                    if (window.DEBUG_GCLID) {
                        console.log('[GCLID] Checkout field already has correct value, skipping');
                    }
                    return;
                }
                // If value changed, allow re-injection
                injectedFields.delete(gclidField);
            }

            // Skip if already has correct value
            if (gclidField.value === gclid) {
                injectedFields.add(gclidField);
                return;
            }

            // Populate the field
            gclidField.value = gclid;
            gclidField.setAttribute('value', gclid);

            // Trigger events to ensure form frameworks pick up the change
            gclidField.dispatchEvent(new Event('input', { bubbles: true }));
            gclidField.dispatchEvent(new Event('change', { bubbles: true }));

            // Mark as injected
            injectedFields.add(gclidField);
            injected = true;
        });

        if (injected && window.DEBUG_GCLID) {
            console.log('[GCLID] Injected into checkout:', gclid, `(${gclidFields.length} field(s))`);
        }

        return injected;
    } catch (e) {
        console.error('[GCLID] Error injecting GCLID into checkout:', e);
        return false;
    }
}

// Make function available globally for use in Twig templates
if (typeof window !== 'undefined') {
    window.injectGclidIntoCheckout = injectGclidIntoCheckout;
}

/**
 * Setup checkout GCLID injection with event listeners
 * Handles dynamic form updates (Sprig, multi-step checkout, etc.)
 */
function setupCheckoutGclidInjection() {
    // Prevent double setup
    if (window._gclidCheckoutSetup) {
        if (window.DEBUG_GCLID) {
            console.warn('[GCLID] Checkout injection already setup, skipping');
        }
        return;
    }
    window._gclidCheckoutSetup = true;

    // Initial injection
    function runInitialInjection() {
        injectGclidIntoCheckout();
    }

    // Run on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', runInitialInjection);
    } else {
        runInitialInjection();
    }

    // Retry after short delay (for Sprig/HTMX panels that render slightly later)
    setTimeout(injectGclidIntoCheckout, 150);

    // Listen for form submit events (safety net - inject right before submit)
    document.addEventListener('submit', function (event) {
        const form = event.target;
        if (form && form.tagName === 'FORM') {
            const gclidField = form.querySelector('[name="fields[googleClickId]"]');
            if (gclidField) {
                // Idempotent - safe to call even if already injected
                injectGclidIntoCheckout();
            }
        }
    }, true); // Capture phase

    // Listen for Sprig/HTMX updates (when partials are swapped)
    // Sprig uses HTMX under the hood, so we can listen to HTMX events
    if (typeof window.htmx !== 'undefined') {
        document.body.addEventListener('htmx:afterSwap', function (event) {
            // Check if swapped content contains checkout form
            if (event.target && event.target.querySelector && event.target.querySelector('[name="fields[googleClickId]"]')) {
                if (window.DEBUG_GCLID) {
                    console.log('[GCLID] Sprig/HTMX swap detected, re-injecting GCLID');
                }
                setTimeout(injectGclidIntoCheckout, 50);
            }
        });

        document.body.addEventListener('htmx:afterSettle', function (event) {
            // After HTMX settles, check for checkout fields
            if (document.querySelector('[name="fields[googleClickId]"]')) {
                if (window.DEBUG_GCLID) {
                    console.log('[GCLID] Sprig/HTMX settled, re-injecting GCLID');
                }
                injectGclidIntoCheckout();
            }
        });
    }

    // Listen for custom checkout step change events (if used)
    document.addEventListener('checkoutStepChange', function () {
        if (window.DEBUG_GCLID) {
            console.log('[GCLID] Checkout step change detected, re-injecting GCLID');
        }
        setTimeout(injectGclidIntoCheckout, 100);
    });

    // Lightweight MutationObserver for dynamic form updates (debounced)
    if (typeof MutationObserver !== 'undefined' && document.body) {
        let debounceTimeout;
        // Cache selector to avoid repeated DOM scanning
        let cachedCheckoutField = null;
        const checkoutFieldSelector = '[name="fields[googleClickId]"]';

        const observer = new MutationObserver(function (mutations) {
            // Debounce to avoid excessive checks
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(function () {
                // Use cached selector if available, otherwise query and cache
                if (!cachedCheckoutField || !document.body.contains(cachedCheckoutField)) {
                    cachedCheckoutField = document.querySelector(checkoutFieldSelector);
                }

                // Only re-inject if checkout field exists
                if (cachedCheckoutField) {
                    injectGclidIntoCheckout();
                }
            }, 200);
        });

        // Observe document.body for new checkout forms
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

// Setup injection listeners
setupCheckoutGclidInjection();
